/**
 * Tab title flash when the current space has a pending awaiting-input
 * and the tab is backgrounded.
 *
 * Scoped to the currently-open space — a different space's awaiting
 * input should NOT flash this tab's title.
 */

import { useEffect } from "react";
import { useCanvasStore } from "@/stores/canvas-store";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import { translate } from "@/i18n";
import { hooksMessages } from "@/i18n/messages/hooks";

export function useAwaitingInputTitle(): void {
  // Only questions whose frame is in the current space count. A count, not
  // the frame list, so unrelated frame changes do not re-render the host.
  const awaitingInputs = useCanvasStore((s) => s.awaitingInputs);
  const pendingCount = useFramesStore((s) => {
    let count = 0;
    for (const [frameId, list] of awaitingInputs) {
      if (s.framesById[frameId]) count += list.length;
    }
    return count;
  });
  const spaceName = useProjectStore((s) => s.currentSpace?.name ?? "Denker");

  useEffect(() => {
    if (pendingCount === 0) return;

    const originalTitle = document.title;
    const alertTitle = translate(hooksMessages, "agentWaitingTitle", {
      space: spaceName,
    });

    const update = () => {
      document.title = document.hidden ? alertTitle : originalTitle;
    };
    update();

    document.addEventListener("visibilitychange", update);
    return () => {
      document.removeEventListener("visibilitychange", update);
      document.title = originalTitle;
    };
  }, [pendingCount, spaceName]);
}
